import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ShieldCheck, Truck, RotateCcw, Minus, Plus, ShoppingBag, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatRupiah } from "@/lib/dummyData";
import { ProductAPI } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart, cartPathFor } from "@/lib/cart";

// Petakan produk backend ke bentuk yang dipakai halaman & keranjang.
function mapProduct(p) {
  return {
    id: p.id,
    sku: p.sku,
    name: p.name,
    category: p.category,
    image: p.image,
    description: p.description,
    price: Number(p.price_non_member),
    memberPrice: Number(p.price_member),
    priceForYou: Number(p.price_for_you),
    stock: Number(p.stock || 0),
    fromMember: p.source_type === "VENDOR_ANGGOTA",
  };
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound | error
  const [qty, setQty] = useState(1);

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    ProductAPI.get(id)
      .then((p) => { if (alive) { setProduct(mapProduct(p)); setStatus("ok"); } })
      .catch((err) => { if (alive) setStatus(err?.response?.status === 404 ? "notfound" : "error"); });
    return () => { alive = false; };
  }, [id]);

  const addToCart = async () => {
    try {
      await addItem(product, qty);
      toast.success(`${qty}× ${product.name} ditambahkan ke keranjang`);
      return true;
    } catch {
      toast.error("Gagal menambah ke keranjang");
      return false;
    }
  };

  const buyNow = async () => {
    if (await addToCart()) navigate(cartPathFor(user));
  };

  if (status !== "ok") {
    return (
      <div className="mx-auto max-w-3xl px-5 lg:px-10 py-20 text-center">
        {status === "loading" ? (
          <p className="text-ink/55 text-sm">Memuat produk…</p>
        ) : (
          <>
            <div className="text-3xl">🌾</div>
            <h1 className="font-display text-2xl font-bold text-ink mt-3">
              {status === "notfound" ? "Produk tidak ditemukan" : "Produk sedang tidak dapat dimuat"}
            </h1>
            <Link to="/toko"><Button className="rounded-full bg-forest text-white mt-6">Kembali ke Toko</Button></Link>
          </>
        )}
      </div>
    );
  }

  const outOfStock = product.stock <= 0;

  return (
    <div className="mx-auto max-w-7xl px-5 lg:px-10 py-8 lg:py-14">
      <button onClick={() => navigate(-1)} className="text-sm text-ink/75 inline-flex items-center gap-1 hover:text-ink mb-6">
        <ChevronLeft size={16} /> Kembali ke Toko
      </button>

      <div className="grid lg:grid-cols-2 gap-10">
        <div className="aspect-square rounded-3xl overflow-hidden border border-stone-soft bg-cream self-start">
          {product.image && <img src={product.image} alt={product.name} className="w-full h-full object-cover" />}
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-amber-warm font-semibold">{product.category}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink mt-2 leading-tight">{product.name}</h1>
          <div className="flex items-center gap-3 mt-3 text-sm">
            <span className={outOfStock ? "text-terracotta font-semibold" : "text-forest font-semibold"}>
              {outOfStock ? "Stok habis" : `Stok ${product.stock}`}
            </span>
            {product.sku && (<><span className="text-ink/55">·</span><span className="text-ink/55">SKU {product.sku}</span></>)}
            {product.fromMember && (<><span className="text-ink/55">·</span><span className="text-ink/75">Produk anggota koperasi</span></>)}
          </div>

          <div className="mt-6 rounded-2xl bg-cream border border-stone-soft p-5">
            <div className="font-display font-bold text-3xl text-ink">{formatRupiah(product.priceForYou || product.price)}</div>
            <div className="mt-2 flex items-center gap-2">
              <Badge className="bg-amber-warm text-ink hover:bg-amber-warm border-0 rounded-full text-[10px] uppercase tracking-wider">Harga Anggota</Badge>
              <span className="font-display font-semibold text-amber-700">{formatRupiah(product.memberPrice)}</span>
              {!user && (
                <Link to="/masuk" className="text-xs text-forest font-semibold inline-flex items-center gap-0.5 hover:gap-1 transition-all ml-2">
                  Login untuk hemat <ArrowUpRight size={12} />
                </Link>
              )}
            </div>
            <div className="text-[11px] text-ink/55 mt-2">Harga sudah termasuk fee layanan koperasi. Belum termasuk ongkos kirim.</div>
          </div>

          <div className="mt-7 flex items-center gap-4">
            <div className="flex items-center border border-stone-soft rounded-full">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 grid place-items-center hover:bg-cream rounded-l-full" data-testid="qty-minus">
                <Minus size={14} />
              </button>
              <div className="w-12 text-center font-display font-semibold" data-testid="qty-value">{qty}</div>
              <button onClick={() => setQty(Math.min(Math.max(1, product.stock), qty + 1))} className="w-10 h-10 grid place-items-center hover:bg-cream rounded-r-full" data-testid="qty-plus">
                <Plus size={14} />
              </button>
            </div>
            <Button
              onClick={addToCart}
              disabled={outOfStock}
              className="flex-1 rounded-full bg-forest hover:bg-forest-dark text-white h-12 text-sm"
              data-testid="product-detail-add-cart"
            >
              <ShoppingBag size={16} /> Tambah ke Keranjang
            </Button>
            <Button variant="outline" disabled={outOfStock} className="rounded-full h-12 px-6 border-ink/15 hover:bg-cream" onClick={buyNow} data-testid="product-detail-buy">
              Beli Sekarang
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-8">
            {[
              { icon: ShieldCheck, label: "Pembayaran aman (escrow)" },
              { icon: Truck, label: "Pengiriman 2–3 hari" },
              { icon: RotateCcw, label: "Komplain 3 hari", to: "/kebijakan-pengembalian" },
            ].map((b) => {
              const body = (
                <div className="rounded-xl border border-stone-soft bg-white p-3 flex items-center gap-2.5 h-full">
                  <b.icon size={18} className="text-forest shrink-0" />
                  <div className="text-xs font-medium text-ink">{b.label}</div>
                </div>
              );
              return b.to ? <Link key={b.label} to={b.to} className="hover:opacity-80">{body}</Link> : <div key={b.label}>{body}</div>;
            })}
          </div>

          <section className="mt-10">
            <h2 className="font-display font-semibold text-lg text-ink">Deskripsi Produk</h2>
            <p className="text-sm text-ink/75 leading-relaxed pt-3 whitespace-pre-line">
              {product.description || `${product.name} — kategori ${product.category}.`}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
